import { equals } from 'ramda'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import mangoLogo from '~/assets/mango-gradientLogo_std.png'
import BackArrowIcon from '~/assets/svgs/icon-back-arrow.svg?react'
import CallIcon from '~/assets/svgs/icon-call-start.svg?react'
import EmailIcon from '~/assets/svgs/icon-email.svg?react'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useUserState } from '~/atoms/UserState'
import { Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import { Loading } from '~/components/Loading'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { TextButton } from '~/components/shared/TextButton'
import { light } from '~/constants/theme'
import { useAnalytics } from '~/hooks/useAnalytics'
import {
  ButtonWrapper,
  EmptyWrapper,
  HelpWrapper,
  LogoImage,
  PbxWrapper,
  TitleWrapper,
  Wrapper,
} from '~/pages/Login/Login.styles'
import { useGetPbxList } from '~/queries/pbxQueries'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { openInNewTab } from '~/utils/functions'

import { Pbx } from '@mangovoice/types'

import { PbxListItem } from './PbxListItem/PbxListItem'

const BackIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <BackArrowIcon {...props} />
)

const p1TextStyles = {
  marginTop: '20px',
  marginBottom: '20px',
  fontWeight: '400',
  fontSize: '16px',
}

const p2TextStyles = {
  marginBottom: '20px',
  fontWeight: '400',
  fontSize: '16px',
}

interface ExtensionPromptProps {
  onBackButtonPressed?: () => void
}
export const PbxPrompt = ({ onBackButtonPressed }: ExtensionPromptProps) => {
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [selectedPbx, setSelectedPbx] = useState<Pbx>()
  const { jwtData } = useAuthenticationState()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [hasNoPbx, setHasNoPbx] = useState(false)
  const { authenticationState } = useAuthenticationState()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const { isErrored: isUserStateErrored, getUserSessionDependencies } =
    useUserState()

  const { data: pbxList, isLoading: pbxListLoading } = useGetPbxList({})

  useEffect(() => {
    if (!authenticationState.appSessionJwts || !selectedPbx) {
      return
    }

    track(AuthenticationAnalyticsEvents.LoginSuccess)
    setLoading(true)

    //tell UserState to fetch the user session dependencies
    //when UserState is done setting dependencies, it will internally flag it's ready - `UnauthenticatedRoute` will then forward the user to their destination
    getUserSessionDependencies(selectedPbx)
  }, [authenticationState.session, selectedPbx])

  if (loading && isUserStateErrored) {
    setErrorMsg('Please try again or contact support.')
    setSelectedPbx(undefined)
    setLoading(false)
  }

  const handlePbxSelected = (pbx: Pbx) => {
    if (!jwtData?.access_token) {
      setErrorMsg('Please try again or contact support.')
    }
    track(AuthenticationAnalyticsEvents.PbxSelected)
    setErrorMsg(null)
    setSelectedPbx(pbx)
    getUserSessionDependencies(pbx)
    navigate('/')
  }

  const handleBackButtonPressed = () => {
    track(AuthenticationAnalyticsEvents.ExtensionBackButtonPressed)
    onBackButtonPressed?.()
  }

  const handleCallSupport = () => {
    track(AuthenticationAnalyticsEvents.CallSupportClicked)
    openInNewTab('tel:4357670882')
  }

  const handleEmailSupport = () => {
    track(AuthenticationAnalyticsEvents.EmailSupportClicked)
    openInNewTab('mailto:support@mangovoice.com')
  }

  useEffect(() => {
    if (!jwtData?.access_token) {
      return
    }
    if (!pbxListLoading && pbxList?.count === 1) {
      handlePbxSelected(pbxList.results[0])
    } else if (!pbxListLoading && pbxList?.count === 0) {
      setHasNoPbx(true)
    }
  }, [pbxList?.count ?? 0, pbxListLoading, jwtData?.access_token])

  return (
    <div>
      {selectedPbx ? (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={mangoLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text="Logging you in..."
                weight="normal"
                color={light.dark800}
              />
              <Loading />
            </TitleWrapper>
          </Wrapper>
        </>
      ) : pbxListLoading ? (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={mangoLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text="Loading pbx's..."
                weight="normal"
                color={light.dark800}
              />
              <Loading />
            </TitleWrapper>
          </Wrapper>
        </>
      ) : hasNoPbx ? (
        <EmptyWrapper>
          <LogoImage alt="logo" src={mangoLogo} />
          <Text style={p1TextStyles}>
            You are not currently a member of any phone system (PBX). To use
            this app, please contact the person in your office who manages the
            phone system to add you.
          </Text>
          <Text style={p2TextStyles}>
            If you believe this is an error, contact Mango Support for further
            assistance.
          </Text>
          <HelpWrapper>
            <TextButton leftIcon={CallIcon} onLinkClick={handleCallSupport}>
              Call support (435) 767-0882
            </TextButton>
            <TextButton leftIcon={EmailIcon} onLinkClick={handleEmailSupport}>
              Email support
            </TextButton>
          </HelpWrapper>
          <ButtonWrapper>
            <MangoButton
              leftIcon={BackIcon}
              leftIconProps={{ width: '12px', height: '12px' }}
              variant={MangoButtonVariant.Default_200}
              onClick={handleBackButtonPressed}
            >
              Back
            </MangoButton>
          </ButtonWrapper>
        </EmptyWrapper>
      ) : (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={mangoLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text="Select your PBX"
                weight="bold"
                color={light.dark800}
              />
            </TitleWrapper>
            <PbxWrapper>
              {pbxList?.results.map((pbx, index) => (
                <PbxListItem
                  key={pbx.id}
                  bottom={equals(index + 1, pbxList.results.length)}
                  pbx={pbx}
                  onSelect={(selectedPbx) => {
                    handlePbxSelected(selectedPbx)
                  }}
                />
              ))}
            </PbxWrapper>
            {errorMsg && (
              <>
                <Text
                  color={light.red200}
                  fontStyle={FontStyle.bSmall}
                  text="Whoops! Something went wrong."
                  align="center"
                />
                <Text
                  color={light.red200}
                  fontStyle={FontStyle.bSmall}
                  text={errorMsg}
                  align="center"
                />
              </>
            )}
            <ButtonWrapper>
              <MangoButton
                leftIcon={BackIcon}
                leftIconProps={{ width: '12px', height: '12px' }}
                variant={MangoButtonVariant.Default_200}
                onClick={handleBackButtonPressed}
              >
                Back
              </MangoButton>
            </ButtonWrapper>
          </Wrapper>
        </>
      )}
    </div>
  )
}
